import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  CalendarDays,
  Grid3X3,
  Heading,
  ImageIcon,
  Plus,
  Type,
  UploadCloud,
  Users,
  CircleHelp,
  Plane,
  Camera,
  House,
  ClipboardCheck,
  Trash2,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { buildWeddingV2RenderModel, WeddingWebsiteV2Renderer } from '@/components/weddingTemplatesV2/WeddingWebsiteV2Renderer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PAGE_ORDER = ['home', 'our_story', 'photos', 'wedding_party', 'qa', 'travel', 'rsvp'];

const PAGE_META = {
  home: { id: 'page-home', label: 'Home', type: 'home', icon: House, defaultTitle: 'Welcome' },
  our_story: { id: 'page-our-story', label: 'Our Story', type: 'our_story', icon: Type, defaultTitle: 'Our Story' },
  photos: { id: 'page-photos', label: 'Photos', type: 'photos', icon: Camera, defaultTitle: 'Photos' },
  wedding_party: { id: 'page-wedding-party', label: 'Wedding Party', type: 'wedding_party', icon: Users, defaultTitle: 'Wedding Party' },
  qa: { id: 'page-qa', label: 'Q&A', type: 'qa', icon: CircleHelp, defaultTitle: 'Q&A' },
  travel: { id: 'page-travel', label: 'Wedding Day', type: 'travel_page', icon: CalendarDays, defaultTitle: 'Wedding Day' },
  rsvp: { id: 'page-rsvp', label: 'RSVP', type: 'rsvp_page', icon: ClipboardCheck, defaultTitle: 'RSVP' },
};

const PAGE_VISIBILITY_ALIASES = {
  home: ['home', 'title'],
  our_story: ['our_story', 'story'],
  photos: ['photos', 'photo_gallery', 'photo'],
  wedding_party: ['wedding_party'],
  qa: ['qa'],
  travel: ['travel_page', 'text_block', 'travel', 'schedule'],
  rsvp: ['rsvp_page', 'rsvp'],
};

const createScheduleEntry = () => ({
  id: `schedule-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  time: '',
  description: '',
  location: '',
});

const normalizeScheduleEntry = (rawEntry, fallbackIndex = 0) => {
  if (!rawEntry || typeof rawEntry !== 'object') {
    return {
      ...createScheduleEntry(),
      id: `schedule-${fallbackIndex}-${Math.random().toString(36).slice(2, 8)}`,
    };
  }

  return {
    id: String(rawEntry.id || `schedule-${fallbackIndex}-${Math.random().toString(36).slice(2, 8)}`).slice(0, 80),
    time: String(rawEntry.time || '').slice(0, 80),
    description: String(rawEntry.description || '').slice(0, 300),
    location: String(rawEntry.location || '').slice(0, 200),
  };
};

const DEFAULT_DESIGN_SETTINGS = {
  template_key: 'editorial-elegant',
  style_pack: 'editorial',
  page_mode: 'single',
  date_format: 'uk-long',
  show_countdown: true,
  typography_style: 'timeless-seriffed',
  spacing_style: 'airy',
  text_alignment: 'center',
  uppercase_headings: false,
  image_orientation: 'horizontal',
  image_fit: 'fill',
  color_theme: 'stone-ivory',
};

const DESIGN_THEME_MAP = {
  'stone-ivory': { pageBg: '#f8f6f2', pageText: '#1f1b16', surface: '#ffffff', border: '#ddd5c9', accent: '#5c0120', accentText: '#ffffff' },
  'blush-champagne': { pageBg: '#fcf5f6', pageText: '#3c2430', surface: '#fffdf9', border: '#e8d4da', accent: '#8f2f56', accentText: '#ffffff' },
  'coastal-pearl': { pageBg: '#f2f7fb', pageText: '#1c3347', surface: '#ffffff', border: '#c7d9e6', accent: '#2d5f86', accentText: '#ffffff' },
  'sage-linen': { pageBg: '#f1f6f1', pageText: '#27372a', surface: '#ffffff', border: '#cedacb', accent: '#3f6a4b', accentText: '#ffffff' },
};

const DESIGN_TYPOGRAPHY_MAP = {
  'timeless-seriffed': { heading: "'Cormorant Garamond', serif", body: "'Manrope', sans-serif" },
  'editorial-grand': { heading: "'Playfair Display', serif", body: "'Manrope', sans-serif" },
  'refined-contemporary': { heading: "'Playfair Display', serif", body: "'Inter', sans-serif" },
  'modern-editorial': { heading: "'Manrope', sans-serif", body: "'Manrope', sans-serif" },
};

const getDesignPreset = (settings = {}) => ({
  theme: DESIGN_THEME_MAP[settings.color_theme] || DESIGN_THEME_MAP['stone-ivory'],
  typography: DESIGN_TYPOGRAPHY_MAP[settings.typography_style] || DESIGN_TYPOGRAPHY_MAP['timeless-seriffed'],
  textAlign: ['left', 'center', 'right'].includes(settings.text_alignment) ? settings.text_alignment : 'center',
});

const ADD_BLOCK_OPTIONS = [
  { type: 'text', label: 'Text Block', icon: Type },
  { type: 'heading', label: 'Title / Heading', icon: Heading },
  { type: 'image', label: 'Single Image', icon: ImageIcon },
  { type: 'gallery', label: 'Photo Gallery', icon: Grid3X3 },
];

const LAYOUT_OPTIONS = [
  { value: 'full-width', label: 'Full width image' },
  { value: 'image-left', label: 'Image left / text right' },
  { value: 'image-right', label: 'Image right / text left' },
];

const DEFAULT_PAGE_STATE = {
  home: {
    id: PAGE_META.home.id,
    key: 'home',
    visible: true,
    title: PAGE_META.home.defaultTitle,
    text: 'Welcome to our wedding website.',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'full-width',
    blocks: [],
  },
  our_story: {
    id: PAGE_META.our_story.id,
    key: 'our_story',
    visible: true,
    title: PAGE_META.our_story.defaultTitle,
    text: '',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'image-left',
    blocks: [],
  },
  photos: {
    id: PAGE_META.photos.id,
    key: 'photos',
    visible: true,
    title: PAGE_META.photos.defaultTitle,
    text: '',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'full-width',
    blocks: [],
  },
  wedding_party: {
    id: PAGE_META.wedding_party.id,
    key: 'wedding_party',
    visible: true,
    title: PAGE_META.wedding_party.defaultTitle,
    text: '',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'image-left',
    blocks: [],
  },
  qa: {
    id: PAGE_META.qa.id,
    key: 'qa',
    visible: true,
    title: PAGE_META.qa.defaultTitle,
    text: '',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'image-right',
    blocks: [],
  },
  travel: {
    id: PAGE_META.travel.id,
    key: 'travel',
    visible: true,
    title: PAGE_META.travel.defaultTitle,
    text: '',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'image-left',
    schedule_entries: [createScheduleEntry()],
    blocks: [],
  },
  rsvp: {
    id: PAGE_META.rsvp.id,
    key: 'rsvp',
    visible: false,
    title: PAGE_META.rsvp.defaultTitle,
    text: 'Please let us know if you can join us.',
    image_url: '',
    image_orientation: 'landscape',
    layout: 'full-width',
    blocks: [],
  },
};

const createBlock = (type) => ({
  id: `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type,
  title: type === 'heading' ? '' : '',
  text: '',
  image_url: '',
  images: [],
  image_orientation: 'landscape',
  layout: 'full-width',
});

const detectImageOrientation = (file) => new Promise((resolve) => {
  if (!file || typeof window === 'undefined') {
    resolve('landscape');
    return;
  }

  const objectUrl = URL.createObjectURL(file);
  const image = new Image();
  image.onload = () => {
    const nextOrientation = image.height > image.width ? 'portrait' : 'landscape';
    URL.revokeObjectURL(objectUrl);
    resolve(nextOrientation);
  };
  image.onerror = () => {
    URL.revokeObjectURL(objectUrl);
    resolve('landscape');
  };
  image.src = objectUrl;
});

const extractUrlsFromBlocks = (blocks) => {
  if (!Array.isArray(blocks)) return [];
  const urls = [];
  blocks.forEach((block) => {
    if (block.type === 'image' && block.image_url) urls.push(block.image_url);
    if (block.type === 'gallery' && Array.isArray(block.images)) {
      block.images.forEach((url) => {
        if (url) urls.push(url);
      });
    }
  });
  return urls;
};

const extractTextFromPage = (page) => {
  if (!page) return '';
  const parts = [];
  if (page.text) parts.push(page.text);
  (page.blocks || []).forEach((block) => {
    if (block.type === 'heading' && block.title) parts.push(block.title);
    if (block.type === 'text' && block.text) parts.push(block.text);
  });
  return parts.join('\n\n').trim();
};

const extractScheduleEntriesFromPage = (page) => {
  if (!page || !Array.isArray(page.schedule_entries)) return [];
  return page.schedule_entries
    .map((entry, index) => normalizeScheduleEntry(entry, index))
    .filter((entry) => entry.time || entry.description || entry.location);
};

const normalizeBlock = (rawBlock) => {
  if (!rawBlock || typeof rawBlock !== 'object') return null;
  const nextType = ['text', 'heading', 'image', 'gallery'].includes(String(rawBlock.type || '').toLowerCase())
    ? String(rawBlock.type).toLowerCase()
    : 'text';

  return {
    id: String(rawBlock.id || `block-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`),
    type: nextType,
    title: String(rawBlock.title || '').slice(0, 160),
    text: String(rawBlock.text || '').slice(0, 8000),
    image_url: String(rawBlock.image_url || ''),
    images: Array.isArray(rawBlock.images) ? rawBlock.images.filter(Boolean).slice(0, 20) : [],
    image_orientation: String(rawBlock.image_orientation || '').toLowerCase() === 'portrait' ? 'portrait' : 'landscape',
    layout: LAYOUT_OPTIONS.some((option) => option.value === rawBlock.layout) ? rawBlock.layout : 'full-width',
  };
};

const normalizePageState = (pageKey, rawPage, fallback = {}) => {
  const base = DEFAULT_PAGE_STATE[pageKey];
  const sourceScheduleEntries = Array.isArray(rawPage?.schedule_entries)
    ? rawPage.schedule_entries
    : (Array.isArray(fallback.schedule_entries) ? fallback.schedule_entries : base.schedule_entries);

  const normalizedScheduleEntries = Array.isArray(sourceScheduleEntries)
    ? sourceScheduleEntries
      .map((entry, index) => normalizeScheduleEntry(entry, index))
      .filter((entry) => entry.time || entry.description || entry.location)
    : [];

  const page = {
    ...base,
    ...rawPage,
    id: base.id,
    key: pageKey,
    visible: typeof rawPage?.visible === 'boolean'
      ? rawPage.visible
      : (typeof fallback?.visible === 'boolean' ? fallback.visible : base.visible),
    title: String(rawPage?.title || fallback.title || base.title || '').slice(0, 120),
    text: String(rawPage?.text || fallback.text || base.text || '').slice(0, 8000),
    image_url: String(rawPage?.image_url || fallback.image_url || base.image_url || ''),
    image_orientation: String(rawPage?.image_orientation || fallback.image_orientation || 'landscape').toLowerCase() === 'portrait' ? 'portrait' : 'landscape',
    layout: LAYOUT_OPTIONS.some((option) => option.value === rawPage?.layout) ? rawPage.layout : (fallback.layout || base.layout || 'full-width'),
    schedule_entries: normalizedScheduleEntries.length > 0 ? normalizedScheduleEntries : (pageKey === 'travel' ? [createScheduleEntry()] : []),
    blocks: Array.isArray(rawPage?.blocks)
      ? rawPage.blocks.map(normalizeBlock).filter(Boolean)
      : (Array.isArray(fallback.blocks) ? fallback.blocks.map(normalizeBlock).filter(Boolean) : []),
  };

  return page;
};

const UploadDropZone = ({ multiple = false, onFilesSelected, testId, helperText = 'Click to upload or drag your image here' }) => {
  const inputId = `${testId}-input`;
  return (
    <label
      htmlFor={inputId}
      className="block border-2 border-dashed border-stone-300 bg-stone-50 p-5 text-center cursor-pointer hover:border-[#5C0120] hover:bg-[#5C0120]/5 transition-colors"
      data-testid={testId}
    >
      <UploadCloud className="mx-auto h-6 w-6 text-stone-500" />
      <p className="mt-2 text-sm font-medium text-stone-700">{helperText}</p>
      <p className="text-xs text-stone-500 mt-1">{multiple ? 'You can select multiple images' : 'Single image upload'}</p>
      <input
        id={inputId}
        type="file"
        className="hidden"
        accept="image/*"
        multiple={multiple}
        onChange={(event) => onFilesSelected(event.target.files)}
        data-testid={`${testId}-file-input`}
      />
    </label>
  );
};

const LayoutSelector = ({ value, onChange, testPrefix }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid={`${testPrefix}-layout-selector`}>
    {LAYOUT_OPTIONS.map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => onChange(option.value)}
        className={`border p-2 text-left transition-colors ${value === option.value ? 'border-[#5C0120] bg-[#5C0120]/10' : 'border-stone-200 bg-white'}`}
        data-testid={`${testPrefix}-layout-option-${option.value}`}
      >
        <div className="h-7 border border-stone-300 bg-stone-100 mb-1" />
        <p className="text-xs text-stone-700">{option.label}</p>
      </button>
    ))}
  </div>
);

const PageAccordionEditor = memo(function PageAccordionEditor({
  page,
  pageKey,
  isOpen,
  onCommit,
  uploadImage,
  uploading,
}) {
  const [localPage, setLocalPage] = useState(page);
  const debounceRef = useRef(null);

  useEffect(() => {
    setLocalPage(page);
  }, [page.id]);

  useEffect(() => {
    if (!isOpen) return undefined;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onCommit(pageKey, localPage);
    }, 800);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [isOpen, localPage, onCommit, pageKey]);

  const addBlock = (type) => {
    setLocalPage((prev) => ({
      ...prev,
      blocks: [...(prev.blocks || []), createBlock(type)],
    }));
  };

  const updateBlock = (blockId, patch) => {
    setLocalPage((prev) => ({
      ...prev,
      blocks: (prev.blocks || []).map((block) => (block.id === blockId ? { ...block, ...patch } : block)),
    }));
  };

  const removeBlock = (blockId) => {
    setLocalPage((prev) => ({
      ...prev,
      blocks: (prev.blocks || []).filter((block) => block.id !== blockId),
    }));
  };

  const addScheduleEntry = () => {
    setLocalPage((prev) => ({
      ...prev,
      schedule_entries: [...(Array.isArray(prev.schedule_entries) ? prev.schedule_entries : []), createScheduleEntry()],
    }));
  };

  const updateScheduleEntry = (entryId, patch) => {
    setLocalPage((prev) => ({
      ...prev,
      schedule_entries: (Array.isArray(prev.schedule_entries) ? prev.schedule_entries : []).map((entry) => (
        entry.id === entryId ? { ...entry, ...patch } : entry
      )),
    }));
  };

  const removeScheduleEntry = (entryId) => {
    setLocalPage((prev) => {
      const currentEntries = Array.isArray(prev.schedule_entries) ? prev.schedule_entries : [];
      if (currentEntries.length <= 1) return prev;
      return {
        ...prev,
        schedule_entries: currentEntries.filter((entry) => entry.id !== entryId),
      };
    });
  };

  const handlePageImageUpload = async (files) => {
    const file = files?.[0];
    if (!file) return;
    const uploaded = await uploadImage([file], pageKey);
    if (!uploaded.length) return;
    setLocalPage((prev) => ({
      ...prev,
      image_url: uploaded[0].url,
      image_orientation: uploaded[0].orientation,
    }));
  };

  const handleBlockImageUpload = async (block, files) => {
    if (!files || files.length === 0) return;
    const uploaded = await uploadImage(Array.from(files), pageKey);
    if (!uploaded.length) return;

    if (block.type === 'image') {
      updateBlock(block.id, {
        image_url: uploaded[0].url,
        image_orientation: uploaded[0].orientation,
      });
      return;
    }

    if (block.type === 'gallery') {
      updateBlock(block.id, {
        images: [...(block.images || []), ...uploaded.map((item) => item.url)],
      });
    }
  };

  return (
    <div className="space-y-6" data-testid={`builder-page-editor-${pageKey}`}>
      <div className="flex items-center justify-between gap-3" data-testid={`builder-page-visibility-row-${pageKey}`}>
        <Label htmlFor={`builder-page-visible-${pageKey}`} className="font-medium">Visible on website</Label>
        <input
          id={`builder-page-visible-${pageKey}`}
          type="checkbox"
          checked={Boolean(localPage.visible)}
          onChange={(event) => setLocalPage((prev) => ({ ...prev, visible: event.target.checked }))}
          data-testid={`builder-page-visible-toggle-${pageKey}`}
        />
      </div>

      {pageKey === 'rsvp' && (
        <div className="space-y-4" data-testid="builder-rsvp-form-preview-card">
          <div className="rounded-sm border border-stone-200 bg-stone-50 p-4 space-y-4">
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="builder-rsvp-form-preview-title">RSVP Form Preview</p>
              <p className="text-xs text-stone-500" data-testid="builder-rsvp-form-preview-subtitle">This is exactly what guests will submit on your website.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="builder-rsvp-form-preview-fields">
              <div>
                <Label>Name *</Label>
                <Input disabled placeholder="Guest name" data-testid="builder-rsvp-preview-name-input" />
              </div>
              <div>
                <Label>Email</Label>
                <Input disabled placeholder="guest@email.com" data-testid="builder-rsvp-preview-email-input" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input disabled placeholder="Phone number" data-testid="builder-rsvp-preview-phone-input" />
              </div>
              <div>
                <Label>Group</Label>
                <select disabled className="w-full p-2 border border-stone-200 rounded-sm bg-white" data-testid="builder-rsvp-preview-group-select">
                  <option>Family - Bride</option>
                  <option>Family - Groom</option>
                  <option>Friends</option>
                  <option>Work Colleagues</option>
                  <option>Other</option>
                </select>
              </div>
              <div>
                <Label>Dietary Requirements</Label>
                <Input disabled placeholder="e.g., Vegetarian" data-testid="builder-rsvp-preview-dietary-input" />
              </div>
              <div>
                <Label>Additional Guests Count</Label>
                <Input disabled type="number" min={0} placeholder="0" data-testid="builder-rsvp-preview-additional-count-input" />
              </div>
            </div>

            <Button type="button" disabled className="w-full bg-[#5C0120] hover:bg-[#5C0120]" data-testid="builder-rsvp-preview-submit-button">
              Submit RSVP
            </Button>
          </div>
        </div>
      )}

      {pageKey !== 'rsvp' && (
      <div>
        <Label htmlFor={`builder-page-title-${pageKey}`}>Title</Label>
        <Input
          id={`builder-page-title-${pageKey}`}
          value={localPage.title || ''}
          onChange={(event) => setLocalPage((prev) => ({ ...prev, title: event.target.value }))}
          data-testid={`builder-page-title-input-${pageKey}`}
        />
      </div>
      )}

      {pageKey !== 'rsvp' && (
      <div>
        <Label htmlFor={`builder-page-text-${pageKey}`}>Text</Label>
        <Textarea
          id={`builder-page-text-${pageKey}`}
          rows={5}
          placeholder="Add context for this page..."
          value={localPage.text || ''}
          onChange={(event) => setLocalPage((prev) => ({ ...prev, text: event.target.value }))}
          data-testid={`builder-page-textarea-${pageKey}`}
        />
      </div>
      )}

      {pageKey === 'travel' && (
        <div className="space-y-3" data-testid="builder-schedule-section-wrap">
          <div className="flex items-center justify-between gap-2">
            <div>
              <p className="text-sm font-semibold text-stone-900" data-testid="builder-schedule-section-title">Schedule</p>
              <p className="text-xs text-stone-500" data-testid="builder-schedule-section-subtitle">Add your wedding day timeline entries.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addScheduleEntry}
              data-testid="builder-schedule-add-row-button"
            >
              <Plus className="h-4 w-4 mr-1" /> Add row
            </Button>
          </div>

          {(Array.isArray(localPage.schedule_entries) ? localPage.schedule_entries : []).map((entry, index) => (
            <div key={entry.id || `schedule-entry-${index}`} className="rounded-sm border border-stone-200 bg-stone-50 p-3 space-y-3" data-testid={`builder-schedule-row-${index}`}>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <Label htmlFor={`builder-schedule-time-${index}`}>Time</Label>
                  <Input
                    id={`builder-schedule-time-${index}`}
                    type="time"
                    lang="en-US"
                    step={60}
                    value={entry.time || ''}
                    onChange={(event) => updateScheduleEntry(entry.id, { time: event.target.value })}
                    data-testid={`builder-schedule-time-input-${index}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`builder-schedule-description-${index}`}>Description</Label>
                  <Input
                    id={`builder-schedule-description-${index}`}
                    value={entry.description || ''}
                    placeholder="Ceremony"
                    onChange={(event) => updateScheduleEntry(entry.id, { description: event.target.value })}
                    data-testid={`builder-schedule-description-input-${index}`}
                  />
                </div>
                <div>
                  <Label htmlFor={`builder-schedule-location-${index}`}>Location</Label>
                  <Input
                    id={`builder-schedule-location-${index}`}
                    value={entry.location || ''}
                    placeholder="Venue name"
                    onChange={(event) => updateScheduleEntry(entry.id, { location: event.target.value })}
                    data-testid={`builder-schedule-location-input-${index}`}
                  />
                </div>
              </div>
              <div className="flex justify-end">
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => removeScheduleEntry(entry.id)}
                  disabled={(localPage.schedule_entries || []).length <= 1}
                  data-testid={`builder-schedule-remove-row-button-${index}`}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      {pageKey !== 'rsvp' && (
      <div>
        <Label>Image upload</Label>
        <UploadDropZone
          onFilesSelected={handlePageImageUpload}
          testId={`builder-page-image-dropzone-${pageKey}`}
          helperText="Click to upload or drag your image here"
        />
        {uploading && <p className="text-xs text-stone-500 mt-2" data-testid={`builder-page-uploading-${pageKey}`}>Uploading...</p>}
        {localPage.image_url && (
          <div className="mt-3 border border-stone-200 p-2" data-testid={`builder-page-image-preview-wrap-${pageKey}`}>
            <img src={localPage.image_url} alt={`${localPage.title || PAGE_META[pageKey].label} image`} className="w-full h-36 object-cover" data-testid={`builder-page-image-preview-${pageKey}`} />
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={() => setLocalPage((prev) => ({ ...prev, image_url: '', image_orientation: 'landscape' }))}
              data-testid={`builder-page-image-remove-${pageKey}`}
            >
              Remove image
            </Button>
          </div>
        )}
      </div>
      )}

      {pageKey !== 'rsvp' && (
      <div>
        <Label>Layout</Label>
        <LayoutSelector
          value={localPage.layout || 'full-width'}
          onChange={(layoutValue) => setLocalPage((prev) => ({ ...prev, layout: layoutValue }))}
          testPrefix={`builder-page-${pageKey}`}
        />
      </div>
      )}

      {pageKey !== 'rsvp' && (
      <div className="space-y-3" data-testid={`builder-page-blocks-wrap-${pageKey}`}>
        {(localPage.blocks || []).map((block, blockIndex) => (
          <article key={block.id} className="border border-stone-200 bg-white p-4 space-y-3" data-testid={`builder-block-${pageKey}-${block.id}`}>
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm font-semibold text-stone-800">
                {block.type === 'text' && 'Text Block'}
                {block.type === 'heading' && 'Title / Heading'}
                {block.type === 'image' && 'Single Image'}
                {block.type === 'gallery' && 'Photo Gallery'}
              </p>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => removeBlock(block.id)}
                data-testid={`builder-block-remove-${pageKey}-${block.id}`}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>

            {block.type === 'text' && (
              <Textarea
                rows={5}
                placeholder="Write your story..."
                value={block.text || ''}
                onChange={(event) => updateBlock(block.id, { text: event.target.value })}
                data-testid={`builder-block-textarea-${pageKey}-${block.id}`}
              />
            )}

            {block.type === 'heading' && (
              <Input
                placeholder="Add a heading..."
                value={block.title || ''}
                onChange={(event) => updateBlock(block.id, { title: event.target.value })}
                data-testid={`builder-block-heading-input-${pageKey}-${block.id}`}
              />
            )}

            {block.type === 'image' && (
              <div className="space-y-3" data-testid={`builder-block-image-wrap-${pageKey}-${block.id}`}>
                <UploadDropZone
                  onFilesSelected={(files) => handleBlockImageUpload(block, files)}
                  testId={`builder-block-image-dropzone-${pageKey}-${block.id}`}
                  helperText="Click to upload or drag your image here"
                />
                {block.image_url && (
                  <div className="border border-stone-200 p-2">
                    <img src={block.image_url} alt={`${localPage.title} image block`} className="w-full h-40 object-cover" data-testid={`builder-block-image-preview-${pageKey}-${block.id}`} />
                  </div>
                )}
                <LayoutSelector
                  value={block.layout || 'full-width'}
                  onChange={(layoutValue) => updateBlock(block.id, { layout: layoutValue })}
                  testPrefix={`builder-block-image-${pageKey}-${block.id}`}
                />
              </div>
            )}

            {block.type === 'gallery' && (
              <div className="space-y-3" data-testid={`builder-block-gallery-wrap-${pageKey}-${block.id}`}>
                <UploadDropZone
                  multiple
                  onFilesSelected={(files) => handleBlockImageUpload(block, files)}
                  testId={`builder-block-gallery-dropzone-${pageKey}-${block.id}`}
                  helperText="Click to upload or drag your image here"
                />
                {(block.images || []).length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid={`builder-block-gallery-grid-${pageKey}-${block.id}`}>
                    {block.images.map((url) => (
                      <img key={url} src={url} alt={`${localPage.title} gallery`} className="w-full aspect-square object-cover border border-stone-200" />
                    ))}
                  </div>
                )}
              </div>
            )}

            <p className="text-xs text-stone-500" data-testid={`builder-block-order-label-${pageKey}-${block.id}`}>Block {blockIndex + 1}</p>
          </article>
        ))}
      </div>
      )}

      {pageKey !== 'rsvp' && (
      <div className="border border-stone-200 bg-stone-50 p-4" data-testid={`builder-page-add-content-wrap-${pageKey}`}>
        <p className="text-sm font-semibold text-stone-900 mb-3">Add Content</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {ADD_BLOCK_OPTIONS.map((option) => {
            const Icon = option.icon;
            return (
              <Button
                key={`${pageKey}-add-${option.type}`}
                type="button"
                variant="outline"
                className="h-auto py-3 flex-col gap-1"
                onClick={() => addBlock(option.type)}
                data-testid={`builder-add-block-${pageKey}-${option.type}`}
              >
                <Icon className="h-4 w-4" />
                <span className="text-xs whitespace-normal text-center">{option.label}</span>
              </Button>
            );
          })}
        </div>
      </div>
      )}
    </div>
  );
});

export const WeddingBuilderAccordionWorkspace = ({ templateKey = 'editorial-elegant' }) => {
  const { token, isAuthenticated, user } = useAuth();

  const [mode, setMode] = useState('edit');
  const [activeAccordion, setActiveAccordion] = useState('design');
  const [designSettings, setDesignSettings] = useState({ ...DEFAULT_DESIGN_SETTINGS, template_key: templateKey });
  const [pages, setPages] = useState(DEFAULT_PAGE_STATE);
  const [contentFields, setContentFields] = useState({
    coupleNames: '',
    weddingDate: '',
    location: 'Gibraltar',
    photoSharingMessage: 'Share your favorite moments from our wedding day.',
  });
  const [publishedLiveUrl, setPublishedLiveUrl] = useState('');
  const [uploadingTarget, setUploadingTarget] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [websiteMeta, setWebsiteMeta] = useState({
    slug: '',
    isPublished: false,
    templateKey: 'romantic-classic',
    colorVariantKey: 'blush-stone',
  });

  const pendingSavesRef = useRef(0);
  const saveSuccessCountRef = useRef(0);
  const saveFailureCountRef = useRef(0);
  const builderSaveTimerRef = useRef(null);
  const draftSaveTimerRef = useRef(null);
  const skipFirstAutosaveRef = useRef(true);
  const skipFirstDraftAutosaveRef = useRef(true);

  const setSaveStatusIfChanged = (nextStatus) => {
    setSaveStatus((prev) => (prev === nextStatus ? prev : nextStatus));
  };

  const beginSave = () => {
    if (pendingSavesRef.current === 0) {
      saveSuccessCountRef.current = 0;
      saveFailureCountRef.current = 0;
    }
    pendingSavesRef.current += 1;
    setSaveStatusIfChanged('saving');
  };

  const endSave = (success = true) => {
    pendingSavesRef.current = Math.max(0, pendingSavesRef.current - 1);
    if (success) {
      saveSuccessCountRef.current += 1;
    } else {
      saveFailureCountRef.current += 1;
    }
    if (pendingSavesRef.current > 0) return;
    const hasAnySuccess = saveSuccessCountRef.current > 0;
    const hasOnlyFailures = !hasAnySuccess && saveFailureCountRef.current > 0;
    setSaveStatusIfChanged(hasOnlyFailures ? 'error' : 'saved');
  };

  const visibleSectionTypes = useMemo(
    () => PAGE_ORDER.filter((key) => pages[key]?.visible).map((key) => PAGE_META[key].type),
    [pages],
  );

  const sectionConfigPayload = useMemo(() => {
    const payload = {};
    PAGE_ORDER.forEach((pageKey) => {
      payload[pageKey] = { ...pages[pageKey] };
    });
    return payload;
  }, [pages]);

  const persistBuilderSettings = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    beginSave();
    try {
      await axios.put(
        `${API}/planner/website/builder-v2-settings`,
        {
          ...designSettings,
          template_key: templateKey,
          sections: visibleSectionTypes,
          section_config: sectionConfigPayload,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      endSave(true);
    } catch {
      endSave(false);
    }
  }, [designSettings, isAuthenticated, sectionConfigPayload, templateKey, token, visibleSectionTypes]);

  const persistDraftContent = useCallback(async () => {
    if (!token || !isAuthenticated) return;
    beginSave();

    const homePage = pages.home;
    const storyPage = pages.our_story;
    const travelPage = pages.travel;
    const rsvpPage = pages.rsvp;
    const photosPage = pages.photos;

    const homeImages = [homePage.image_url, ...extractUrlsFromBlocks(homePage.blocks)].filter(Boolean);
    const storyImages = extractUrlsFromBlocks(storyPage.blocks);
    const allPhotos = [...homeImages, ...extractUrlsFromBlocks(photosPage.blocks)].filter(Boolean);
    const scheduleEntries = extractScheduleEntriesFromPage(travelPage);
    const scheduleItems = scheduleEntries.map((entry) => ({
      time: entry.time,
      title: entry.description || entry.location || 'Wedding event',
      notes: entry.location || '',
    }));

    const coupleParts = contentFields.coupleNames.split('&').map((part) => part.trim()).filter(Boolean);
    const partnerOneName = coupleParts[0] || contentFields.coupleNames.trim() || 'Partner One';
    const partnerTwoName = coupleParts[1] || 'Partner Two';

    try {
      await axios.put(
        `${API}/planner/website`,
        {
          partner_one_name: partnerOneName,
          partner_two_name: partnerTwoName,
          wedding_date: contentFields.weddingDate,
          wedding_location: contentFields.location,
          hero_image_url: homePage.image_url || homeImages[0] || '',
          story_text: extractTextFromPage(storyPage),
          show_schedule: scheduleItems.length > 0,
          schedule_items: scheduleItems,
          travel_info_text: extractTextFromPage(travelPage),
          photo_cta_text: extractTextFromPage(rsvpPage) || contentFields.photoSharingMessage,
          template_key: websiteMeta.templateKey || 'romantic-classic',
          color_variant_key: websiteMeta.colorVariantKey || 'blush-stone',
          slug: websiteMeta.slug || '',
          is_published: Boolean(websiteMeta.isPublished),
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      endSave(true);
    } catch {
      endSave(false);
    }
  }, [contentFields, isAuthenticated, pages, token, websiteMeta]);

  useEffect(() => {
    if (!token || !isAuthenticated) {
      setIsHydrated(false);
      return;
    }

    let cancelled = false;

    const runHydrationAttempt = async () => {
      skipFirstAutosaveRef.current = true;
      skipFirstDraftAutosaveRef.current = true;

      try {
        const [settingsRes, websiteRes] = await Promise.all([
          axios.get(`${API}/planner/website/builder-v2-settings`, { headers: { Authorization: `Bearer ${token}` } }),
          axios.get(`${API}/planner/website`, { headers: { Authorization: `Bearer ${token}` } }),
        ]);

        const savedSettings = settingsRes.data?.settings || {};
        const website = websiteRes.data || {};

        setWebsiteMeta({
          slug: (website.slug || '').trim(),
          isPublished: Boolean(website.is_published),
          templateKey: (website.template_key || 'romantic-classic').trim() || 'romantic-classic',
          colorVariantKey: (website.color_variant_key || 'blush-stone').trim() || 'blush-stone',
        });

        setPublishedLiveUrl(website.is_published && website.slug ? `/weddings/${website.slug}` : '');

        setDesignSettings((prev) => ({
          ...prev,
          ...savedSettings,
          template_key: templateKey,
        }));

        const rawConfig = savedSettings.section_config || {};
        const visibleTypes = new Set(Array.isArray(savedSettings.sections) ? savedSettings.sections : []);

        const pageState = {};
        PAGE_ORDER.forEach((pageKey) => {
          const legacyRaw = pageKey === 'home'
            ? (rawConfig.home || rawConfig.hero)
            : pageKey === 'our_story'
              ? (rawConfig.our_story || rawConfig.story)
              : pageKey === 'travel'
                ? (rawConfig.travel || rawConfig.travel_page)
                : pageKey === 'rsvp'
                  ? (rawConfig.rsvp || rawConfig.rsvp_page)
                  : rawConfig[pageKey];

          const fallback = {
            title: PAGE_META[pageKey].defaultTitle,
            text: pageKey === 'our_story'
              ? (website.story_text || '')
              : pageKey === 'travel'
                ? (website.travel_info_text || '')
                : pageKey === 'rsvp'
                  ? (website.photo_cta_text || 'Please let us know if you can join us.')
                  : '',
            image_url: pageKey === 'home' ? (website.hero_image_url || '') : '',
            blocks: [],
          };

          if (pageKey === 'photos') {
            const existingPhotos = Array.isArray(website.content?.photos) ? website.content.photos.filter(Boolean) : [];
            if (existingPhotos.length > 0) {
              fallback.blocks = [{
                id: 'legacy-photo-gallery',
                type: 'gallery',
                title: '',
                text: '',
                image_url: '',
                images: existingPhotos,
                image_orientation: 'landscape',
                layout: 'full-width',
              }];
            }
          }

          if (pageKey === 'our_story' && !legacyRaw?.blocks && website.story_text) {
            fallback.blocks = [{
              id: 'legacy-story-text',
              type: 'text',
              title: '',
              text: website.story_text,
              image_url: '',
              images: [],
              image_orientation: 'landscape',
              layout: 'full-width',
            }];
          }

          if (pageKey === 'travel') {
            const legacySchedule = Array.isArray(website.content?.schedule)
              ? website.content.schedule
                .map((item, index) => ({
                  id: item?.id || `legacy-schedule-${index}`,
                  time: item?.time || '',
                  description: item?.title || '',
                  location: item?.notes || '',
                }))
                .filter((item) => item.time || item.description || item.location)
              : [];

            if (legacySchedule.length > 0) {
              fallback.schedule_entries = legacySchedule;
            }
          }

          const normalized = normalizePageState(pageKey, legacyRaw, fallback);
          if (visibleTypes.size > 0) {
            normalized.visible = PAGE_VISIBILITY_ALIASES[pageKey].some((alias) => visibleTypes.has(alias));
          }
          pageState[pageKey] = normalized;
        });

        setPages(pageState);

        setContentFields({
          coupleNames: (website.content?.couple_names || user?.couple_names || `${website.partner_one_name || ''}${website.partner_two_name ? ` & ${website.partner_two_name}` : ''}` || '').trim(),
          weddingDate: (website.content?.date || website.wedding_date || user?.wedding_date || '').trim(),
          location: (website.content?.location || website.wedding_location || 'Gibraltar').trim(),
          photoSharingMessage: (website.content?.photo_sharing_message || website.photo_cta_text || 'Share your favorite moments from our wedding day.').trim(),
        });
        return true;
      } catch {
        return false;
      }
    };

    const hydrateData = async () => {
      const firstAttempt = await runHydrationAttempt();
      if (cancelled) return;

      if (firstAttempt) {
        setIsHydrated(true);
        return;
      }

      const secondAttempt = await runHydrationAttempt();
      if (cancelled) return;

      if (secondAttempt) {
        setIsHydrated(true);
      } else {
        toast.error('Could not load website builder data.');
      }
    };

    hydrateData();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, templateKey, token, user?.couple_names, user?.wedding_date]);

  useEffect(() => {
    if (!isHydrated || !token || !isAuthenticated) return;
    if (skipFirstAutosaveRef.current) {
      skipFirstAutosaveRef.current = false;
      return;
    }

    if (builderSaveTimerRef.current) clearTimeout(builderSaveTimerRef.current);
    builderSaveTimerRef.current = setTimeout(() => {
      persistBuilderSettings();
    }, 800);

    return () => {
      if (builderSaveTimerRef.current) clearTimeout(builderSaveTimerRef.current);
    };
  }, [designSettings, isHydrated, isAuthenticated, persistBuilderSettings, sectionConfigPayload, token, visibleSectionTypes]);

  useEffect(() => {
    if (!isHydrated || !token || !isAuthenticated) return;
    if (skipFirstDraftAutosaveRef.current) {
      skipFirstDraftAutosaveRef.current = false;
      return;
    }

    if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    draftSaveTimerRef.current = setTimeout(() => {
      persistDraftContent();
    }, 800);

    return () => {
      if (draftSaveTimerRef.current) clearTimeout(draftSaveTimerRef.current);
    };
  }, [contentFields, isHydrated, isAuthenticated, persistDraftContent, pages, token]);

  const updatePage = useCallback((pageKey, nextPageData) => {
    setPages((prev) => ({
      ...prev,
      [pageKey]: {
        ...prev[pageKey],
        ...nextPageData,
      },
    }));
  }, []);

  const uploadImages = useCallback(async (files, pageKey) => {
    if (!files || files.length === 0 || !token || !isAuthenticated) return [];
    const uploaded = [];
    setUploadingTarget(pageKey);

    try {
      for (const file of files) {
        const orientation = await detectImageOrientation(file);
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', pageKey === 'home' ? 'main' : 'story');

        const response = await axios.post(`${API}/wedding/upload-photo`, formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data',
          },
        });

        const uploadedUrl = response.data?.url;
        if (uploadedUrl) {
          uploaded.push({ url: uploadedUrl, orientation });
        }
      }
    } catch {
      toast.error('Could not upload image right now.');
    } finally {
      setUploadingTarget('');
    }

    return uploaded;
  }, [isAuthenticated, token]);

  const handlePublishWebsite = async () => {
    if (!token || !isAuthenticated) {
      toast.error('Please sign in as a couple before publishing.');
      return;
    }

    if (!contentFields.coupleNames.trim()) {
      toast.error('Please add couple names before publishing.');
      return;
    }

    const homePage = pages.home;
    const storyPage = pages.our_story;
    const travelPage = pages.travel;
    const photosPage = pages.photos;
    const rsvpPage = pages.rsvp;
    const scheduleEntries = extractScheduleEntriesFromPage(travelPage);

    const homeImages = [homePage.image_url, ...extractUrlsFromBlocks(homePage.blocks)].filter(Boolean);
    const storyImages = [storyPage.image_url, ...extractUrlsFromBlocks(storyPage.blocks)].filter(Boolean);
    const allPhotos = [...homeImages, ...extractUrlsFromBlocks(photosPage.blocks)].filter(Boolean);

    setPublishing(true);
    try {
      const response = await axios.post(
        `${API}/wedding/publish`,
        {
          template_id: templateKey,
          style_settings: {
            ...designSettings,
            template_key: templateKey,
            sections: visibleSectionTypes,
            section_config: sectionConfigPayload,
          },
          content: {
            couple_names: contentFields.coupleNames,
            date: contentFields.weddingDate,
            location: contentFields.location,
            story: extractTextFromPage(storyPage),
            schedule: scheduleEntries.map((entry) => ({
              time: entry.time,
              title: entry.description || entry.location || 'Wedding event',
              notes: entry.location || '',
            })),
            travel_info: extractTextFromPage(travelPage),
            main_photo: homePage.image_url || homeImages[0] || '',
            photos: allPhotos,
            story_images: storyImages,
            photo_sharing_message: extractTextFromPage(rsvpPage) || contentFields.photoSharingMessage,
          },
          rsvp_enabled: pages.rsvp?.visible !== false,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );

      setPublishedLiveUrl(response.data?.live_url || '');
      setWebsiteMeta((prev) => ({
        ...prev,
        slug: response.data?.slug || prev.slug,
        isPublished: true,
      }));
      toast.success('Your wedding website is live');
    } catch {
      toast.error('Could not publish your wedding website right now.');
    } finally {
      setPublishing(false);
    }
  };

  const saveStatusText = saveStatus === 'saving'
    ? 'Saving...'
    : saveStatus === 'saved'
      ? 'Saved ✓'
      : saveStatus === 'error'
        ? 'Save failed'
        : 'All changes auto-save';

  const activeDesignPreset = getDesignPreset(designSettings);

  const previewContent = useMemo(() => {
    const photoBlocks = Array.isArray(pages.photos?.blocks) ? pages.photos.blocks : [];
    const fallbackPhotos = photoBlocks
      .filter((block) => block?.type === 'gallery' && Array.isArray(block.images))
      .flatMap((block) => block.images)
      .filter(Boolean);

    return {
      couple_names: contentFields.coupleNames,
      date: contentFields.weddingDate,
      location: contentFields.location,
      main_photo: pages.home?.image_url || '',
      photos: fallbackPhotos,
      photo_sharing_message: extractTextFromPage(pages.rsvp),
      schedule: extractScheduleEntriesFromPage(pages.travel).map((entry) => ({
        time: entry.time,
        title: entry.description || entry.location || 'Wedding event',
        notes: entry.location || '',
      })),
    };
  }, [contentFields, pages]);

  const previewRenderModel = useMemo(
    () => buildWeddingV2RenderModel({
      sectionConfig: sectionConfigPayload,
      designSettings,
      content: previewContent,
      rsvpEnabled: pages.rsvp?.visible !== false,
    }),
    [sectionConfigPayload, designSettings, previewContent, pages.rsvp?.visible],
  );

  const pageCards = PAGE_ORDER.map((pageKey) => {
    const page = pages[pageKey];
    const Icon = PAGE_META[pageKey].icon;
    return (
      <button
        key={page.id}
        type="button"
        onClick={() => setActiveAccordion(pageKey)}
        className={`w-full border text-left p-4 transition-colors ${activeAccordion === pageKey ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white'}`}
        data-testid={`builder-page-trigger-${pageKey}`}
      >
        <div className="flex items-center gap-2">
          <Icon className="h-4 w-4 text-[#5C0120]" />
          <p className="text-sm font-semibold text-stone-900">{PAGE_META[pageKey].label}</p>
        </div>
        <p className="text-xs text-stone-500 mt-1">{page.visible ? 'Visible' : 'Hidden'}</p>
      </button>
    );
  });

  if (!isHydrated) {
    return (
      <div className="rounded-sm border border-stone-200 bg-white p-6" data-testid="builder-accordion-loading">
        Loading website builder...
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-100" data-testid="builder-page-based-workspace">
      <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8 space-y-6">
        <header className="border border-stone-200 bg-white p-4 flex flex-wrap items-center justify-between gap-3" data-testid="builder-page-topbar">
          <div>
            <p className="text-xs uppercase tracking-[0.18em] text-[#5C0120] font-semibold">Wedding Website Builder</p>
            <h1 className="text-2xl font-semibold text-stone-900" data-testid="builder-page-title">Build your wedding website page by page</h1>
          </div>
          <div className="flex items-center gap-2" data-testid="builder-mode-toggle-wrap">
            <Button
              type="button"
              variant={mode === 'edit' ? 'default' : 'outline'}
              className={mode === 'edit' ? 'bg-[#5C0120] hover:bg-[#5C0120]/90' : ''}
              onClick={() => setMode('edit')}
              data-testid="builder-mode-edit-button"
            >
              Edit Mode
            </Button>
            <Button
              type="button"
              variant={mode === 'preview' ? 'default' : 'outline'}
              className={mode === 'preview' ? 'bg-[#5C0120] hover:bg-[#5C0120]/90' : ''}
              onClick={() => setMode('preview')}
              data-testid="builder-mode-preview-button"
            >
              Preview Mode
            </Button>
          </div>
          <div className="flex items-center gap-2" data-testid="builder-save-publish-wrap">
            <p className="text-sm text-stone-600 w-[165px] text-right" data-testid="builder-save-status-text">{saveStatusText}</p>
            <Button
              type="button"
              onClick={handlePublishWebsite}
              disabled={publishing}
              className="hover:opacity-90"
              style={{ backgroundColor: activeDesignPreset.theme.accent, color: activeDesignPreset.theme.accentText }}
              data-testid="builder-publish-button"
            >
              {publishing ? 'Publishing...' : 'Publish website'}
            </Button>
            {publishedLiveUrl && (
              <a href={publishedLiveUrl} target="_blank" rel="noreferrer" data-testid="builder-live-url-link">
                <Button type="button" variant="outline">Open website</Button>
              </a>
            )}
          </div>
        </header>

        {mode === 'edit' ? (
          <>
            <div className="max-w-[680px] mx-auto space-y-3" data-testid="builder-page-collapsed-column">
              <button
                type="button"
                onClick={() => setActiveAccordion('design')}
                className={`w-full border text-left p-4 transition-colors ${activeAccordion === 'design' ? 'border-[#5C0120] bg-[#5C0120]/5' : 'border-stone-200 bg-white'}`}
                data-testid="builder-trigger-design"
              >
                <p className="text-sm font-semibold text-stone-900">Design Settings</p>
                <p className="text-xs text-stone-500 mt-1">Typography, spacing, color, couple basics</p>
              </button>
              {pageCards}
            </div>

            <div className="max-w-[1120px] mx-auto" data-testid="builder-expanded-wrap">
              <div className="border border-stone-200 bg-white p-6 sm:p-8 lg:p-10" data-testid="builder-expanded-panel">
                {activeAccordion === 'design' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="builder-design-settings-panel">
                    <div>
                      <Label htmlFor="builder-couple-names-input">Couple names</Label>
                      <Input
                        id="builder-couple-names-input"
                        value={contentFields.coupleNames}
                        onChange={(event) => setContentFields((prev) => ({ ...prev, coupleNames: event.target.value }))}
                        data-testid="builder-couple-names-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="builder-wedding-date-input">Wedding date</Label>
                      <Input
                        id="builder-wedding-date-input"
                        type="date"
                        value={contentFields.weddingDate}
                        onChange={(event) => setContentFields((prev) => ({ ...prev, weddingDate: event.target.value }))}
                        data-testid="builder-wedding-date-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="builder-location-input">Location</Label>
                      <Input
                        id="builder-location-input"
                        value={contentFields.location}
                        onChange={(event) => setContentFields((prev) => ({ ...prev, location: event.target.value }))}
                        data-testid="builder-location-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="builder-color-theme-select">Color theme</Label>
                      <select
                        id="builder-color-theme-select"
                        value={designSettings.color_theme}
                        onChange={(event) => setDesignSettings((prev) => ({ ...prev, color_theme: event.target.value }))}
                        className="w-full rounded-md border border-stone-300 bg-white p-2"
                        data-testid="builder-color-theme-select"
                      >
                        <option value="stone-ivory">Stone Ivory</option>
                        <option value="blush-champagne">Blush Champagne</option>
                        <option value="coastal-pearl">Coastal Pearl</option>
                        <option value="sage-linen">Sage Linen</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="builder-typography-select">Typography</Label>
                      <select
                        id="builder-typography-select"
                        value={designSettings.typography_style}
                        onChange={(event) => setDesignSettings((prev) => ({ ...prev, typography_style: event.target.value }))}
                        className="w-full rounded-md border border-stone-300 bg-white p-2"
                        data-testid="builder-typography-select"
                      >
                        <option value="timeless-seriffed">Timeless Serif</option>
                        <option value="editorial-grand">Editorial Grand</option>
                        <option value="refined-contemporary">Refined Contemporary</option>
                        <option value="modern-editorial">Modern Editorial</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="builder-spacing-select">Spacing</Label>
                      <select
                        id="builder-spacing-select"
                        value={designSettings.spacing_style}
                        onChange={(event) => setDesignSettings((prev) => ({ ...prev, spacing_style: event.target.value }))}
                        className="w-full rounded-md border border-stone-300 bg-white p-2"
                        data-testid="builder-spacing-select"
                      >
                        <option value="compact">Compact</option>
                        <option value="balanced">Balanced</option>
                        <option value="airy">Airy</option>
                        <option value="airy-plus">Airy Plus</option>
                      </select>
                    </div>
                    <div>
                      <Label htmlFor="builder-text-alignment-select">Text alignment</Label>
                      <select
                        id="builder-text-alignment-select"
                        value={designSettings.text_alignment || 'center'}
                        onChange={(event) => setDesignSettings((prev) => ({ ...prev, text_alignment: event.target.value }))}
                        className="w-full rounded-md border border-stone-300 bg-white p-2"
                        data-testid="builder-text-alignment-select"
                      >
                        <option value="left">Left</option>
                        <option value="center">Center</option>
                        <option value="right">Right</option>
                      </select>
                    </div>
                  </div>
                ) : (
                  <PageAccordionEditor
                    key={pages[activeAccordion].id}
                    page={pages[activeAccordion]}
                    pageKey={activeAccordion}
                    isOpen
                    onCommit={updatePage}
                    uploadImage={uploadImages}
                    uploading={uploadingTarget === activeAccordion}
                  />
                )}
              </div>
            </div>
          </>
        ) : (
          <WeddingWebsiteV2Renderer
            renderModel={previewRenderModel}
            testIdPrefix="builder-readonly-preview"
          />
        )}
      </div>
    </div>
  );
};
